import { TimetableReportType } from '@bp/planung-graphql-types';
import dayjs from 'dayjs';
import i18next from 'i18next';
import { action, makeAutoObservable, toJS } from 'mobx';
import { CardType, isCoexistableCard } from '../../components/TimetableVersion/graphql/types';
import { GridType } from '../../pages/Timetable/Plan/TimetableVersion/Board/types';
import {
  PlaceTimetableCardDocument,
  PlaceTimetableCardMutation,
  PlaceTimetableCardMutationVariables,
  SetTimetableCardsLockedDocument,
  SetTimetableCardsLockedMutation,
  SetTimetableCardsLockedMutationVariables,
  UnplaceTimetableCardsDocument,
  UnplaceTimetableCardsMutation,
  UnplaceTimetableCardsMutationVariables,
} from '../../types/planung-graphql-client-defs';
import { availabilityResourceTypeToWarningType } from '../../utils/getWarningTypeTranslation';
import { DisplayReportType, downloadDisplayReport } from '../../utils/pdfReport';
import type { ParsedTimefoldAnalysis } from '../../utils/timetable/parseTimefoldScoreAnalysis';
import { ControlResult } from '../../utils/timetable/types';
import { urqlClient } from '../../utils/urqlClient';
import { TimetableCardUuid } from '../GridCardsStore';
import { persistSettings } from '../userConfig/persistSettings';
import type { UserConfigStore } from '../userConfig/UserConfigStore';
import type { PinboardStore } from './PinboardStore';
import {
  BoardLayout,
  CardEditModalType,
  CellItem,
  ColumnItem,
  ConflictListModalType,
  ConflictModalType,
  ContraintModalType,
  ControlModeType,
  DayItem,
  FindingSlotHighlight,
  HighlightType,
  HourItem,
  PickedCardType,
  RoomItem,
  RowItem,
  TestModalType,
  TimefoldAnalyzeModalType,
  TimefoldAnalyzeModeType,
  TimeGridEntryUuidWeekday,
  Warnings,
} from './PinboardStoreTypes';
import { Day } from './TimeAvailabilityStore';

/** Kontexte, die die Vorschau-Karte darstellen kann (ohne Fächer). */
export type PreviewMapContext = 'classes' | 'teachers' | 'rooms';

/**
 * Felder des Stundenplan-Boards, die pro Person gespeichert werden.
 *
 * Nicht dabei: alles Transiente (Pick/Pin/Auswahl, Modals, Suchtext, Findings),
 * `hiddenRows` (bewusst nicht gespeichert), `isFullscreen` (die Fullscreen-API
 * braucht eine Nutzergeste) und `context` — der laeuft ueber `selectContext`,
 * weil die Kartenlogik den Kontext beim Laden mehrfach selbst setzt.
 */
export const PINBOARD_SETTING_KEYS = [
  'rowHeight',
  'cardColorBySubject',
  'showTeacherInsteadOfSubject',
  'filterFunctionClasses',
  'groupRoomsByBuilding',
  'boardLayout',
  'boardTopHeight',
  'boardSplitWidth',
  'boardPickerWidth',
  'boardPickerHeight',
  'showPreviewMap',
  'previewMapContext',
  'previewMapAuto',
  'boardPreviewMapWidth',
  'boardPreviewMapHeight',
  'showCardInfo',
  'showTimetablePreview',
  'boardTimetablePreviewTeachersWidth',
  'boardTimetablePreviewTeachersHeight',
  'boardTimetablePreviewRoomsWidth',
  'boardTimetablePreviewRoomsHeight',
  'timetablePreviewCellMinHeight',
  'showEpochRanges',
  'showPause',
  'highlightMode',
] as const;

export class PinboardUIStore {
  public pickedCard: PickedCardType | null = null;
  public pinnedCard: string | null = null;
  public hoveredCard: string | null = null;

  public selectedRows: RowItem[] = [];
  public hiddenRows: Set<string> = new Set();
  public selectedColumns: Set<TimeGridEntryUuidWeekday> = new Set();
  public rowsToHighlight: Set<string> = new Set();

  public context: GridType = 'classes';

  public highlightMode: HighlightType[] = ['warnings'];

  public conflictModal: ConflictModalType = { conflicts: null };
  public conflictListModal: ConflictListModalType = { isOpen: false };
  public constraintModal: ContraintModalType = { isOpen: false };
  public testModal: TestModalType = { isOpen: false, isLoading: false, result: null, fromGenerator: false };
  public timefoldAnalyzeModal: TimefoldAnalyzeModalType = {
    isOpen: false,
    isLoading: false,
    result: null,
    error: null,
    partialClassScope: false,
  };
  public cardEditModal: CardEditModalType = { isOpen: false, isLoading: false };
  public controlMode: ControlModeType = { isActive: false, isLoading: false, expand: false };
  public timefoldAnalyzeMode: TimefoldAnalyzeModeType = { isActive: false, isLoading: false, expand: false };

  /** Single highlight set for findings — control and timefold modes are mutually exclusive. */
  public findingHighlights: Set<TimetableCardUuid> = new Set();

  /**
   * Zell-Highlights aktiver Findings (z. B. Hohlstunden, gebrochene Verfügbarkeiten), pro Zeile.
   * Äußerer Key = Row-UUID, innerer Key = `${hourUuid}:${day}`, Wert = Hinweistext fürs Tooltip.
   * Per-Row-Maps werden als Ganzes ersetzt: GridCells lesen nur den Eintrag ihrer eigenen Zeile,
   * dadurch re-rendern bei Änderungen nur Zellen der betroffenen Zeilen (analog needsPickedCard-Guard).
   */
  public findingSlotHighlightsByRow: Map<string, ReadonlyMap<TimeGridEntryUuidWeekday, string>> = new Map();

  /** Candidate row UUIDs to scroll into view (e.g. when a finding is highlighted); the grid scrolls the
   *  first rendered one and clears this (P1-7). */
  public scrollToRows: string[] = [];

  public isFullscreen: boolean = false;
  public rowHeight: number = 32;
  public cardColorBySubject: boolean = false;
  /** Zeigt auf den Kärtchen das LuL-Kürzel statt des Fach-Kürzels. */
  public showTeacherInsteadOfSubject: boolean = false;
  public filterFunctionClasses: boolean = false;
  /** Gruppiert die Raum-Zeilen im Raum-Kontext nach Gebäuden (nur wenn Gebäude gepflegt sind). */
  public groupRoomsByBuilding: boolean = true;

  /**
   * Anordnung von Auswahl (CardPicker + CardInfo) und Board. Siehe {@link BoardLayout}.
   * `boardTopHeight` (px) gilt im `stacked`-Layout, `boardSplitWidth` (%) im `split`-Layout —
   * beide per verschiebbarer Trennlinie im Board justierbar.
   */
  public boardLayout: BoardLayout = 'stacked';
  public boardTopHeight: number = 200;
  public boardSplitWidth: number = 34;
  /** Anteil des CardPickers am Auswahl-Bereich (Rest = CardInfo), je Layout eigene Achse. */
  public boardPickerWidth: number = 70;
  public boardPickerHeight: number = 60;

  /**
   * Optionale Vorschau-Karte (Mini-Map des Plans) zwischen CardPicker und CardInfo.
   * Standard aus; eingeblendet ist sie per Trennlinie justierbar (eigene Achse je Layout) und
   * kann unabhängig vom Board-Kontext Klassen/Lehrkräfte/Räume darstellen.
   */
  public showPreviewMap: boolean = false;
  public previewMapContext: PreviewMapContext = 'classes';
  /**
   * Automatik: Karte in der Hand → die Vorschau zeigt statt eines Kontexts mehrere Abschnitte, die
   * zum Board-Kontext passen (siehe `previewMapSections`). Eine manuelle Kontext-Wahl schaltet sie ab.
   */
  public previewMapAuto: boolean = true;
  public boardPreviewMapWidth: number = 30;
  public boardPreviewMapHeight: number = 30;

  /**
   * Optionale Stundenplan-Vorschau (Lehrer- + Raumplan der Karte in der Hand) im Auswahl-Bereich.
   * Anders als die PreviewMap zeigt sie klassische Wochen-Stundenpläne (Tage als Spalten, Stunden
   * von oben nach unten) und ist zugleich Ablageziel: ein Klick auf einen Slot steckt die Karte
   * (im Raumplan zusätzlich Raum des aktiven Tabs). Nur im Klassen-Kontext mit Karte in der Hand.
   */
  /** CardInfo-Panel im Auswahl-Bereich; ausblendbar, um den Stundenplan-Panels Platz zu geben. */
  public showCardInfo: boolean = true;
  public showTimetablePreview: boolean = false;
  // Lehrer- und Raumplan sind zwei eigenständige, unabhängig justierbare Panels (ein gemeinsamer
  // Toggle). Jedes hat seine eigene Größe je Achse (Breite im stacked-, Höhe im split-Layout).
  public boardTimetablePreviewTeachersWidth: number = 25;
  public boardTimetablePreviewTeachersHeight: number = 25;
  public boardTimetablePreviewRoomsWidth: number = 25;
  public boardTimetablePreviewRoomsHeight: number = 25;
  /** Mindesthöhe der Zellen in den Stundenplan-Vorschauen (px), per Regler im Panel-Menü. */
  public timetablePreviewCellMinHeight: number = 20;

  public showEpochRanges: boolean = true;
  public cardSearchString: string = '';
  public showPause: boolean = false;
  public columns: ColumnItem[] = [];

  // Private state for pick/place coordination
  public _oldCardRooms: Map<string, RoomItem> = new Map();
  public _oldRoomUuidToReplace: string | null = null;
  /**
   * Raum-Kontext, Kopiermodus (Shift beim Pick): Räume werden beim Ablegen additiv gesetzt und
   * die Karte bleibt in der Hand (dieselbe Karte in mehrere Räume). Zurücklegen behält alle.
   */
  public _pickIsCopy: boolean = false;
  /** Beim (Nicht-Kopier-)Pick aus einer Raum-Zeile entfernter Raum – für Restore bei Escape. */
  public _removedRoomOnPick: RoomItem | null = null;
  public _oldSlotCardUuids: Set<string> = new Set();
  public _oldCardPlacement: CellItem | null = null;
  public _lastPickedCard: PickedCardType | null = null;
  private _cardToSwitch: string | null = null;
  private _lastSelectedRow: RowItem | null = null;
  private _controlModeResult: ControlResult | null = null;

  /** Plan signature captured when each check result was produced; compared against the live signature to
   *  detect staleness after the user edits the plan (P2-5). null = no result of that kind. */
  private _findingsPlanSignature: { control: string | null; timefold: string | null; test: string | null } = {
    control: null,
    timefold: null,
    test: null,
  };

  /** Last successful Timefold analyze response (structured); cleared via bar or new run. */
  public timefoldParsedAnalysis: ParsedTimefoldAnalysis | null = null;

  private disposeSettings: (() => void) | null = null;
  private userConfig: UserConfigStore | null = null;

  constructor(
    private root: PinboardStore,
    userConfig: UserConfigStore | null = null,
  ) {
    makeAutoObservable(this, {
      setCardEditModal: action.bound,
      removeOtherRooms: action.bound,
      setTimefoldAnalyzeModal: action.bound,
      setTimefoldParsedAnalysis: action.bound,
      clearTimefoldParsedAnalysis: action.bound,
      setTimefoldAnalyzeModeActive: action.bound,
      setTimefoldAnalyzeModeLoading: action.bound,
      expandTimefoldAnalyzeResults: action.bound,
      addFindingHighlights: action.bound,
      removeFindingHighlights: action.bound,
    });

    this.userConfig = userConfig;
    if (userConfig) {
      this.disposeSettings = persistSettings(this, userConfig, 'pinboard', PINBOARD_SETTING_KEYS);
    }
  }

  /** Beendet das Beobachten der persistierten Felder. */
  dispose() {
    this.disposeSettings?.();
    this.disposeSettings = null;
  }

  get showWarningsHighlight() {
    return this.highlightMode.includes('warnings');
  }

  get showRoomsHighlight() {
    return this.highlightMode.includes('rooms');
  }

  get showConflictsHighlight() {
    return this.highlightMode.includes('conflicts');
  }

  setIsFullscreen(isFullscreen: boolean) {
    this.isFullscreen = isFullscreen;
  }

  setScrollToRows(rowUuids: string[]) {
    this.scrollToRows = rowUuids;
  }

  clearScrollToRows() {
    this.scrollToRows = [];
  }

  setCardColorBySubject(value: boolean) {
    this.cardColorBySubject = value;
  }

  setShowTeacherInsteadOfSubject(value: boolean) {
    this.showTeacherInsteadOfSubject = value;
  }

  setCardSearchString(value: string) {
    this.cardSearchString = value;
  }

  setFilterFunctionClasses(value: boolean) {
    this.filterFunctionClasses = value;
  }

  setGroupRoomsByBuilding(value: boolean) {
    this.groupRoomsByBuilding = value;
  }

  setShowEpochRanges(value: boolean) {
    this.showEpochRanges = value;
  }

  setContext(type: GridType) {
    if (this.context !== type) {
      this.pickedCard = null;
      this._oldRoomUuidToReplace = null;
      this._removedRoomOnPick = null;
      this._pickIsCopy = false;
      this._oldCardPlacement = null;
      this._oldCardRooms.clear();
      this.selectedColumns.clear();
      this.rowsToHighlight.clear();
      this.selectedRows = [];
      this.hiddenRows.clear();
      this.context = type;
    }
    this.root.conflictStore.setConflicts();
  }

  /**
   * Bewusster Kontextwechsel durch die Nutzerin oder den Nutzer — nur dieser wird
   * gespeichert. `setContext` bleibt ohne Persistenz, weil die Kartenlogik es
   * beim Laden selbst aufruft und den gespeicherten Wert sonst ueberschreibt.
   */
  selectContext(type: GridType) {
    this.setContext(type);
    this.userConfig?.updateSettings('pinboard', { context: type });
  }

  /**
   * Stellt den zuletzt gewaehlten Kontext her. Wird erst **nach** dem initialen
   * Laden der Karten aufgerufen, weil `initData` den Kontext selbst setzt.
   */
  restorePersistedContext() {
    // `context` hat im Schema bewusst keinen Default: Ohne bewusste Auswahl ist der
    // Wert `undefined`, und der von der Kartenlogik gesetzte Kontext bleibt stehen.
    const stored = this.userConfig?.getSettings('pinboard').context;
    if (stored && stored !== this.context) {
      this.setContext(stored);
    }
  }

  setConflictModalConflicts(conflicts: Warnings | null) {
    this.conflictModal.conflicts = conflicts;
  }

  setConflictListModalOpen(isOpen: boolean) {
    this.conflictListModal.isOpen = isOpen;
  }

  setConstraintModalOpen(setOpen: boolean) {
    this.constraintModal.isOpen = setOpen;
  }

  async setTestModalOpen(setOpen: boolean) {
    this.testModal.result = null;
    this._findingsPlanSignature.test = null;
    this.testModal.isOpen = setOpen;
    this.controlMode.expand = false;
    this.timefoldAnalyzeMode.expand = false;
  }

  async setTestModalFromGenerator(fromGenerator: boolean) {
    this.testModal.fromGenerator = fromGenerator;
  }

  async setTestModalLoading(isLoading: boolean) {
    this.testModal.isLoading = isLoading;
  }

  async setTestModalResult(result: import('../../utils/timetable/types').GenericWarnings) {
    this.testModal.result = result;
    this._findingsPlanSignature.test = this.root.findingsPlanSignature;
  }

  setTimefoldAnalyzeModal(patch: Partial<TimefoldAnalyzeModalType>) {
    this.timefoldAnalyzeModal = { ...this.timefoldAnalyzeModal, ...patch };
  }

  setCardEditModal(modal: CardEditModalType) {
    if (modal.isOpen === false) {
      modal.isLoading = false;
    }
    this.cardEditModal = { ...modal };
    this.controlMode.expand = false;
    this.timefoldAnalyzeMode.expand = false;
  }

  setControlModeActive(setActive: boolean) {
    if (setActive && !this.controlMode.isActive) {
      // Moduswechsel: Highlights des anderen Modus (inkl. Zellen) zurücksetzen
      this.timefoldAnalyzeMode.isActive = false;
      this.timefoldAnalyzeMode.expand = false;
      this.findingHighlights = new Set();
      this.clearFindingSlotHighlights();
    }
    this.controlMode.isActive = setActive;
    this.controlMode.expand = false;
    if (!setActive) {
      this.findingHighlights = new Set();
      this.clearFindingSlotHighlights();
    }
  }

  setTimefoldAnalyzeModeActive(setActive: boolean) {
    if (setActive && !this.timefoldAnalyzeMode.isActive) {
      // Moduswechsel: Highlights des anderen Modus (inkl. Zellen) zurücksetzen
      this.controlMode.isActive = false;
      this.controlMode.expand = false;
      this.findingHighlights = new Set();
      this.clearFindingSlotHighlights();
    }
    this.timefoldAnalyzeMode.isActive = setActive;
    this.timefoldAnalyzeMode.expand = false;
    if (!setActive) {
      this.findingHighlights = new Set();
      this.clearFindingSlotHighlights();
    }
  }

  /** Liest den Finding-Hinweis einer Zelle; undefined ⇔ Zelle nicht gehighlightet. */
  getFindingSlotHint(rowUuid: string, hourUuid: string, day: string): string | undefined {
    return this.findingSlotHighlightsByRow.get(rowUuid)?.get(`${hourUuid}:${day}` as TimeGridEntryUuidWeekday);
  }

  addFindingSlotHighlights(slots: FindingSlotHighlight[]) {
    const byRow = new Map<string, Map<TimeGridEntryUuidWeekday, string>>();
    for (const slot of slots) {
      const inner = byRow.get(slot.rowUuid) ?? new Map<TimeGridEntryUuidWeekday, string>();
      inner.set(`${slot.hourUuid}:${slot.day}` as TimeGridEntryUuidWeekday, slot.hint);
      byRow.set(slot.rowUuid, inner);
    }
    byRow.forEach((inner, rowUuid) => {
      const existing = this.findingSlotHighlightsByRow.get(rowUuid);
      const merged = new Map(existing ?? []);
      inner.forEach((hint, key) => merged.set(key, hint));
      // Row-Map als Ganzes ersetzen — nur Zellen dieser Zeile reagieren
      this.findingSlotHighlightsByRow.set(rowUuid, merged);
    });
  }

  removeFindingSlotHighlights(slots: FindingSlotHighlight[]) {
    const byRow = new Map<string, Set<TimeGridEntryUuidWeekday>>();
    for (const slot of slots) {
      const keys = byRow.get(slot.rowUuid) ?? new Set<TimeGridEntryUuidWeekday>();
      keys.add(`${slot.hourUuid}:${slot.day}` as TimeGridEntryUuidWeekday);
      byRow.set(slot.rowUuid, keys);
    }
    byRow.forEach((keys, rowUuid) => {
      const existing = this.findingSlotHighlightsByRow.get(rowUuid);
      if (!existing) {
        return;
      }
      const merged = new Map(existing);
      keys.forEach((key) => merged.delete(key));
      if (merged.size === 0) {
        this.findingSlotHighlightsByRow.delete(rowUuid);
      } else {
        this.findingSlotHighlightsByRow.set(rowUuid, merged);
      }
    });
  }

  clearFindingSlotHighlights() {
    if (this.findingSlotHighlightsByRow.size > 0) {
      this.findingSlotHighlightsByRow.clear();
    }
  }

  setTimefoldAnalyzeModeLoading(isLoading: boolean) {
    this.timefoldAnalyzeMode.isLoading = isLoading;
  }

  expandTimefoldAnalyzeResults(expand: boolean) {
    this.timefoldAnalyzeMode.expand = expand;
  }

  addFindingHighlights(highlights: string[]) {
    highlights.forEach((highlight) => this.findingHighlights.add(highlight as TimetableCardUuid));
  }

  removeFindingHighlights(highlights: string[]) {
    highlights.forEach((highlight) => this.findingHighlights.delete(highlight as TimetableCardUuid));
  }

  setControlModeLoading(isLoading: boolean) {
    this.controlMode.isLoading = isLoading;
  }

  setControlModeResult(result: ControlResult | null) {
    this._controlModeResult = result;
    this._findingsPlanSignature.control = result ? this.root.findingsPlanSignature : null;
  }

  expandControlResults(expand: boolean) {
    this.controlMode.expand = expand;
  }

  async setHighlightMode(mode: HighlightType) {
    const index = this.highlightMode.findIndex((m) => m === mode);
    if (index === -1) {
      this.highlightMode.push(mode);
    } else {
      this.highlightMode.splice(index, 1);
    }
  }

  setPinnedCard(cardUuid: string | null) {
    this.pinnedCard = cardUuid;
  }

  setHoveredCard(cardUuid: string) {
    this.hoveredCard = cardUuid;
  }

  getCardToShowOnInfo() {
    if (this.pinnedCard) {
      return this.root.getCardByUuid(this.pinnedCard);
    }
    if (this.hoveredCard) {
      return this.root.getCardByUuid(this.hoveredCard);
    }
  }

  placeCard(_cell: CellItem) {
    if (!this.root.isEditable) return;
    if (!this.pickedCard) return;
    const canBeDropped = this.root.canBeDropped(
      this.pickedCard.cardUuid,
      this.pickedCard.duration,
      _cell.hour.value,
      _cell.day.value,
    );
    if (!canBeDropped) return;

    const day = _cell.day;
    const row = _cell.row;
    const card = this.root.getCardByUuid(this.pickedCard.cardUuid);

    const allCells = this.root.getAllHoursBetween(
      _cell.hour.value,
      this.root.getFollowers(_cell.hour.value, card?.duration ?? 1)?.pop()?.value ?? _cell.hour.value,
    );
    const endTimeItem = allCells.filter((c, i, all) => i === all.length - 1)[0];

    const currentCellCards: string[] = [];
    const currentCellsCards: Map<string, string[]> = new Map();

    allCells.forEach((cell) => {
      this.root.cardsBySlotAndResource.get(`${cell.value}:${day.value}:${row.value}`)?.forEach((c) => {
        currentCellCards.push(c);
      });
    });

    // Koexistenz (elective/attendance): solche Karten dürfen sich eine Cell teilen – aber nur mit
    // anderen koexistierbaren Karten. Mischen mit normalen Karten wird geblockt (Drop verworfen),
    // und bei Koexistenz entfällt der Karten-Swap ("taschen"). Rooms-Kontext bleibt unberührt.
    let coexistPlacement = false;
    if (card && this.context !== 'rooms') {
      const existingCellCards = [...new Set(currentCellCards)]
        .filter((u) => u !== card.uuid)
        .map((u) => this.root.getCardByUuid(u))
        .filter((c): c is CardType => !!c);

      if (existingCellCards.length > 0) {
        const pickedCoexistable = isCoexistableCard(card);
        const allExistingCoexistable = existingCellCards.every(isCoexistableCard);
        const anyExistingCoexistable = existingCellCards.some(isCoexistableCard);

        if (pickedCoexistable && allExistingCoexistable) {
          coexistPlacement = true;
        } else if (pickedCoexistable || anyExistingCoexistable) {
          // Mischen koexistierbar <-> normal ist nicht erlaubt -> Drop blockieren (Karte bleibt gewählt)
          return;
        }
      }
    }

    if (card) {
      allCells.forEach((hour) => {
        card.classes.forEach((c) => {
          const cardUuids: string[] = [];
          this.root.cardsBySlotAndResource.get(`${hour.value}:${day.value}:${c.uuid}`)?.forEach((u) => {
            cardUuids.push(u);
          });
          currentCellsCards.set(c.uuid, cardUuids);
        });
      });

      if (this.context === 'rooms') {
        // Raum zusätzlich setzen (Map dedupliziert). Verschiebemodus: der aufgenommene Quell-Raum
        // wurde beim Pick entfernt → netto ein Tausch, danach ist die Karte abgelegt. Kopiermodus:
        // Karte bleibt in der Hand, um sie in weitere Räume zu setzen.
        this.root.setRoomsForCards([card], [{ ...row, group: '', inUse: false }], true);
        if (!this._pickIsCopy) {
          this.pickedCard = null;
          this._removedRoomOnPick = null;
          this._oldRoomUuidToReplace = null;
        }
        return;
      }

      if (!this.root.cardRooms.has(card.uuid) && this.root.defaultCardRooms.get(card.uuid)?.counter === 1) {
        const roomToSet = this.root.defaultCardRooms.get(card.uuid)?.allUnique[0];
        if (roomToSet) {
          this.root.setRoomsForCards([card], [roomToSet]);
        }
      }

      allCells.forEach((hour) => {
        this.root.cardTeacher.get(card.uuid)?.forEach((t) => {
          const current = this.root.cardsBySlotAndResource.get(`${hour.value}:${day.value}:${t}`) ?? new Set();
          this.root.cardsBySlotAndResource.set(`${hour.value}:${day.value}:${t}`, current.add(card.uuid));
        });

        this.root.cardRooms.get(card.uuid)?.forEach((r) => {
          const current = this.root.cardsBySlotAndResource.get(`${hour.value}:${day.value}:${r.value}`) ?? new Set();
          this.root.cardsBySlotAndResource.set(`${hour.value}:${day.value}:${r.value}`, current.add(card.uuid));
        });

        this.root.cardClasses.get(card.uuid)?.forEach((c) => {
          const current = this.root.cardsBySlotAndResource.get(`${hour.value}:${day.value}:${c}`) ?? new Set();
          this.root.cardsBySlotAndResource.set(`${hour.value}:${day.value}:${c}`, current.add(card.uuid));
        });
      });

      if (currentCellCards && currentCellCards.length > 0 && !row.isFunctionClass && !coexistPlacement) {
        if (!this._cardToSwitch) {
          this._cardToSwitch = this._findSwapCardUuid(card, currentCellCards, currentCellsCards);
        }
      }

      if (endTimeItem) {
        this.root.freeCardsSet.delete(card.uuid);
        allCells.forEach((hour) => {
          const current = this.root.placedCards.get(`${hour.value}:${day.value}`);
          current?.set(card.uuid, {
            cardUuid: card.uuid,
            label: card.subject?.shortName ?? '',
            hasWarning: false,
            isStart: hour.value === _cell.hour.value,
            isEnd: hour.value === endTimeItem?.value,
            duration: card.duration ?? 1,
            cardRows: card.badgeCardRows,
            labelColor: card.badgeCardTextColor,
          });
        });

        this.root.placedCardsSet.add(card.uuid);
        this.root.placedCardsDay.set(card.uuid, day.value);
        const allCellValues = allCells.map((e) => e.value);
        this.root.placedCardsTimeGridEntries.set(card.uuid, allCellValues);
        this.root.cardStore.updateCardPlacement(
          card.uuid,
          allCellValues[0],
          allCellValues[allCellValues.length - 1],
          day.value,
        );
        this.root.cardStore.invalidateResolvedCard(card.uuid);

        this.root.cardStore._setNewCardResources(card.uuid);
        // Only re-check the placed card + neighbours sharing the same resources/slot
        const newSlotCards = this.root.conflictStore._getAffectedCardsForCard(card.uuid);
        this.root.conflictStore.updateConflictsForCards(new Set([...newSlotCards, ...this._oldSlotCardUuids]));
        this._oldSlotCardUuids = new Set();

        void urqlClient
          .mutation<PlaceTimetableCardMutation, PlaceTimetableCardMutationVariables>(PlaceTimetableCardDocument, {
            cardUuid: card.uuid,
            weekday: day.value,
            startTimeGridEntryUuid: _cell.hour.value ?? '',
            endTimeGridEntryUuid: endTimeItem.value ?? '',
          })
          .toPromise();
      }
    }

    if (this.pickedCard?.fromStack && !this._cardToSwitch) {
      const next = this.root.getCardsStack().get(`${card?.lesson?.uuid}:${card?.duration ?? 1}`);
      if (next && next.length > 0) {
        const nextCard = next[0];
        this.pickCard(
          {
            cardUuid: nextCard.uuid,
            cardRows: nextCard.badgeCardRows,
            duration: nextCard.duration ?? 1,
            counter: 1,
            fromStack: true,
            label: nextCard.subject?.shortName ?? '',
            labelColor: nextCard.badgeCardTextColor,
            position: { x: 0, y: 0 },
          },
          undefined,
          true,
        );
      } else {
        this.pickedCard = null;
      }
    } else if (this._cardToSwitch) {
      const switchCard = this.root.getCardByUuid(this._cardToSwitch);
      this._cardToSwitch = null;
      if (switchCard) {
        this.pickCard(
          {
            cardUuid: switchCard.uuid,
            cardRows: switchCard.badgeCardRows,
            duration: switchCard.duration ?? 1,
            counter: 1,
            fromStack: false,
            label: switchCard.subject?.shortName ?? '',
            labelColor: switchCard.badgeCardTextColor,
            position: { x: 0, y: 0 },
          },
          {
            day: day,
            row: row,
            hour: {
              isPause: false,
              label: '',
              value: switchCard.startTimeGridEntry?.uuid ?? '',
            },
          },
          false,
        );
      }
    } else {
      this.pickedCard = null;
    }

    if (card && !this.pickedCard && !row.isFunctionClass) {
      this.showConflictModal(card);
    }
  }

  /**
   * Karte, mit der beim Ablegen getauscht wird: die erste nicht fixierte Karte im Ziel, deren
   * Klassen/Teilungen zur abgelegten Karte passen. `null` = kein Tausch, die Karte landet
   * zusätzlich im Ziel. Gemeinsame Regel für `placeCard` und `getSwapPreview`.
   */
  private _findSwapCardUuid(
    card: CardType,
    currentCellCards: string[],
    currentCellsCards: Map<string, string[]>,
  ): string | null {
    const everyCellHasOneCard = Array.from(currentCellsCards.values()).every((c) => c.length === 1);
    for (const uuid of currentCellCards) {
      const cardToCheck = this.root.getCardByUuid(uuid);
      if (!cardToCheck || this.root.lockedCards.has(cardToCheck.uuid)) continue;
      const canBeSwitched = card.lessonClasses.every((lc) => {
        return cardToCheck.lessonClasses.every((lc2) => {
          if (lc.usedDivision || lc2.usedDivision) {
            return (
              (lc.usedDivision?.uuid === lc2.usedDivision?.uuid &&
                lc.groups.every((g, i) => g.uuid === lc2.groups[i]?.uuid)) ||
              (everyCellHasOneCard && lc.usedDivision?.uuid !== lc2.usedDivision?.uuid)
            );
          }
          return lc.class.uuid === lc2.class.uuid;
        });
      });
      if (canBeSwitched) return cardToCheck.uuid;
    }
    return null;
  }

  /**
   * Was beim Ablegen der Karte in eine BELEGTE Zelle passiert – für die Tausch-Markierung beim
   * Aufnehmen. Spiegelt `placeCard`: Liegen im Ziel (Zeile, alle abgedeckten Stunden) Karten,
   * wird mit der ersten passenden, nicht fixierten getauscht (`swapCardUuid`); sie wandert in die
   * Hand und zählt für Konflikte nicht mehr. Alle übrigen Karten bleiben liegen
   * (`remainingCardUuids`). `overlapsRemaining` meldet die Klassen-Überschneidung mit einer
   * liegenbleibenden Karte, die `checkCardAvailability` im Klassen-Kontext nicht prüft (ganze
   * Klasse gegen ganze Klasse oder gegen eine Gruppe; Gruppe gegen Gruppe prüft die
   * Verfügbarkeit selbst).
   *
   * `null`: Zelle frei, Raum-/Funktionsklassen-Kontext, nicht ablegbar oder Koexistenz
   * (elective/attendance) – dort wird nicht getauscht.
   */
  getSwapPreview(
    cardUuid: string,
    duration: number,
    cell: CellItem,
  ): { swapCardUuid: string | null; remainingCardUuids: string[]; overlapsRemaining: boolean } | null {
    if (this.context === 'rooms' || cell.row.isFunctionClass) return null;
    const card = this.root.getCardByUuid(cardUuid);
    if (!card) return null;
    const followers = this.root.getFollowers(cell.hour.value, duration);
    if (!followers) return null;
    const allCells = this.root.getAllHoursBetween(cell.hour.value, followers[followers.length - 1]?.value ?? '');

    const currentCellCards: string[] = [];
    allCells.forEach((hour) => {
      this.root.cardsBySlotAndResource.get(`${hour.value}:${cell.day.value}:${cell.row.value}`)?.forEach((u) => {
        if (u !== cardUuid) currentCellCards.push(u);
      });
    });
    if (currentCellCards.length === 0) return null;

    const occupants = [...new Set(currentCellCards)]
      .map((u) => this.root.getCardByUuid(u))
      .filter((c): c is CardType => !!c);
    if (isCoexistableCard(card) || occupants.some(isCoexistableCard)) return null;

    const currentCellsCards: Map<string, string[]> = new Map();
    allCells.forEach((hour) => {
      card.classes.forEach((c) => {
        currentCellsCards.set(c.uuid, [
          ...(this.root.cardsBySlotAndResource.get(`${hour.value}:${cell.day.value}:${c.uuid}`) ?? []),
        ]);
      });
    });

    const swapCardUuid = this._findSwapCardUuid(card, currentCellCards, currentCellsCards);
    const remaining = occupants.filter((c) => c.uuid !== swapCardUuid);
    const isWholeClassIn = (c: CardType, classUuid: string) => {
      const lessonClass = c.lessonClasses.find((lc) => lc.class.uuid === classUuid);
      return !lessonClass?.usedDivision || lessonClass.groups.length === 0;
    };
    const overlapsRemaining =
      this.context === 'classes' &&
      remaining.some((c) => isWholeClassIn(card, cell.row.value) || isWholeClassIn(c, cell.row.value));

    return { swapCardUuid, remainingCardUuids: remaining.map((c) => c.uuid), overlapsRemaining };
  }

  showConflictModal(card: CardType) {
    if (this.root.cardConflicts.has(card.uuid)) {
      const conflicts = this.root.cardConflicts.get(card.uuid);
      if (conflicts) {
        const warnings: Warnings = [];
        conflicts.forEach((conflict, key) => {
          if (
            this.context === 'classes' &&
            conflict.type !== 'class' &&
            conflict.type !== 'divisionGroup' &&
            conflict.placed.size > 0
          ) {
            const name = this.root.getNameForUuid(key, conflict.type);
            const warningType = availabilityResourceTypeToWarningType(conflict.type, conflict.blocked, conflict.maybe);
            if (warningType !== 'overlappingTeachingBlock') {
              warnings.push({
                type: warningType,
                data: {
                  name: name,
                  uuid: key,
                  involvedCards: new Set([...conflict.placed].filter((u) => u !== card.uuid)),
                },
              });
            }
          }
        });
        this.conflictModal = {
          conflicts: warnings,
          placedCardUuid: card.uuid,
        };
      }
    }
  }

  toggleAllRowSelection = () => {
    const visibleRows = this.root.getRows().filter((r) => !this.hiddenRows.has(r.value));
    this.selectedRows.length > 0 && this.selectedRows.length === visibleRows.length
      ? this.selectRows([])
      : this.selectRows(visibleRows);
  };

  rowClickHandler(e: React.MouseEvent<HTMLDivElement>, row: RowItem) {
    if (this.context !== 'rooms') {
      if (this._lastSelectedRow === row) {
        this.selectRows(this.selectedRows.filter((r) => r !== row));
        this._lastSelectedRow = null;
        return;
      } else if (e.altKey || e.ctrlKey) {
        this.selectRows([...this.selectedRows, row]);
      } else if (e.shiftKey) {
        const prevIndex = this._lastSelectedRow
          ? this.root.getRows().findIndex((rowItem) => rowItem.value === this._lastSelectedRow?.value)
          : 0;
        const clickIndex = this.root.getRows().findIndex((rowItem) => rowItem.value === row.value);
        const selected =
          clickIndex > prevIndex
            ? this.root.getRows().slice(prevIndex, clickIndex + 1)
            : this.root.getRows().slice(clickIndex, prevIndex + 1);
        this.selectRows([
          ...this.selectedRows,
          ...selected.filter(
            (item) => this.selectedRows.findIndex((selectedRow) => selectedRow.value === item.value) === -1,
          ),
        ]);
      } else if (this.selectedRows.length === 1 && this.selectedRows[0] === row) {
        this.selectRows([]);
      } else {
        this.selectRows([row]);
      }
      this._lastSelectedRow = row;
    } else {
      this._lastSelectedRow = null;
      if (this.selectedRows.length === 1 && this.selectedRows[0] === row) {
        this.selectRows([]);
      } else {
        this.selectRows([row]);
      }
    }
  }

  selectRows(rows: RowItem[]) {
    this.selectedRows = rows;
  }

  toggleRowsVisibility(rows: RowItem[]) {
    const allHidden = rows.every((r) => this.hiddenRows.has(r.value));
    rows.forEach((r) => {
      if (allHidden) {
        this.hiddenRows.delete(r.value);
      } else {
        this.hiddenRows.add(r.value);
      }
    });
  }

  showAllRows() {
    this.hiddenRows.clear();
  }

  lockCardsAtDay(day: DayItem) {
    if (!this.root.isEditable) return;
    const cards = this.root.getCardsAtDay(day);
    cards.forEach((card) => this.root.lockCard(card.uuid));
  }

  unlockCardsAtDay(day: DayItem) {
    if (!this.root.isEditable) return;
    const cards = this.root.getCardsAtDay(day);
    cards.forEach((card) => this.root.unlockCard(card.uuid));
  }

  removeCardsInSelectedRows() {
    if (!this.root.isEditable) return;
    this._oldCardPlacement = null;
    this.pickedCard = null;
    const validCards = new Set<string>();
    this.selectedRows.forEach((row) => {
      this.root.days.forEach((day) => {
        this.root.timeGridEntries?.forEach((entry) => {
          if (this.context === 'classes') {
            const cards = this.root.cardsBySlotAndResource.get(`${entry.uuid}:${day.value}:${row.value}`);
            cards?.forEach((cardUuid) => {
              if (!this.root.lockedCards.has(cardUuid)) {
                this.root.cardStore._removeCardFromPlacedCards(cardUuid);
                validCards.add(cardUuid);
                this.root.freeCardsSet.add(cardUuid);
              }
            });
          }
        });
      });
    });

    this.root.conflictStore.removeConflicts();

    if (validCards.size > 0) {
      void urqlClient
        .mutation<UnplaceTimetableCardsMutation, UnplaceTimetableCardsMutationVariables>(
          UnplaceTimetableCardsDocument,
          { cardUuids: Array.from(validCards) },
        )
        .toPromise();
    }
  }

  unlockCardsInSelectedRows() {
    if (!this.root.isEditable) return;
    const validCards = new Set<string>();
    const placedCardsToLock: string[] = [];
    this.selectedRows.forEach((row) => {
      this.root.days.forEach((day) => {
        this.root.timeGridEntries?.forEach((entry) => {
          if (this.context === 'classes') {
            const cards = this.root.cardsBySlotAndResource.get(`${entry.uuid}:${day.value}:${row.value}`);
            cards?.forEach((cardUuid) => {
              validCards.add(cardUuid);
            });
          }
        });
      });
    });
    this.root.placedCardsDay.forEach((day, cardUuid) => {
      if (validCards.has(cardUuid)) {
        placedCardsToLock.push(cardUuid);
      }
    });

    placedCardsToLock.forEach((cardUuid) => {
      this.root.lockedCards.delete(cardUuid);
    });

    if (placedCardsToLock.length > 0) {
      void urqlClient
        .mutation<SetTimetableCardsLockedMutation, SetTimetableCardsLockedMutationVariables>(
          SetTimetableCardsLockedDocument,
          { cardUuids: placedCardsToLock, locked: false },
        )
        .toPromise();
    }
  }

  lockCardsInSelectedRows = () => {
    if (!this.root.isEditable) return;
    const validCards = new Set<string>();
    const placedCardsToLock: string[] = [];
    this.selectedRows.forEach((row) => {
      this.root.days.forEach((day) => {
        this.root.timeGridEntries?.forEach((entry) => {
          if (this.context === 'classes') {
            const cards = this.root.cardsBySlotAndResource.get(`${entry.uuid}:${day.value}:${row.value}`);
            cards?.forEach((cardUuid) => {
              validCards.add(cardUuid);
            });
          }
        });
      });
    });
    this.root.placedCardsDay.forEach((day, cardUuid) => {
      if (validCards.has(cardUuid)) {
        placedCardsToLock.push(cardUuid);
      }
    });

    placedCardsToLock.forEach((cardUuid) => {
      this.root.lockedCards.set(cardUuid, true);
    });

    if (placedCardsToLock.length > 0) {
      void urqlClient
        .mutation<SetTimetableCardsLockedMutation, SetTimetableCardsLockedMutationVariables>(
          SetTimetableCardsLockedDocument,
          { cardUuids: placedCardsToLock, locked: true },
        )
        .toPromise();
    }
  };

  lowlightHour(hour: { label: string; value: string }): boolean {
    const pickedCard = this.pickedCard;
    if (pickedCard) {
      const followers = this.root.getFollowers(hour.value, pickedCard?.duration ?? 1);
      return followers === null;
    }

    return false;
  }

  lowlightDay(day: DayItem): boolean {
    const pickedCard = this.pickedCard;
    if (!pickedCard) {
      return false;
    }
    return this.root.hours.every((hour) => {
      return this.lowlightHour(hour);
    });
  }

  removeOtherRooms(conflict: Warnings[0], conflictIndex: number) {
    if (!this.root.isEditable) return;
    if ((conflict.type !== 'roomNotAvailable' && conflict.type !== 'overlappingRooms') || !this._lastPickedCard) {
      return;
    }
    this.root.conflictStore.removeConflicts();

    const roomUuid = conflict.data?.uuid;
    const cards = conflict.data?.involvedCards;
    const cardUuidsToRemoveRoom: string[] = [];

    // filter out last placed card
    cards?.forEach((cardUuid) => {
      if (cardUuid !== this._lastPickedCard?.cardUuid) {
        cardUuidsToRemoveRoom.push(cardUuid);
      }
    });

    if (roomUuid) {
      this.root.removeRoomsFromCards(
        cardUuidsToRemoveRoom.map((c) => ({ uuid: c })),
        [{ value: roomUuid }],
      );
    }
    this.conflictModal.conflicts = this.conflictModal.conflicts?.filter((_, index) => index !== conflictIndex) ?? null;
    this.root.conflictStore.setConflicts();
  }

  public placeWithoutRoom(conflict: Warnings[0], conflictIndex: number) {
    if (!this.root.isEditable) return;
    if ((conflict.type !== 'roomNotAvailable' && conflict.type !== 'overlappingRooms') || !this._lastPickedCard) {
      return;
    }
    this.root.conflictStore.removeConflicts();
    const roomUuid = conflict.data?.uuid;
    if (roomUuid) {
      this.root.removeRoomsFromCards([{ uuid: this._lastPickedCard.cardUuid }], [{ value: roomUuid }]);
    }
    this.conflictModal.conflicts = this.conflictModal.conflicts?.filter((_, index) => index !== conflictIndex) ?? null;
    this.root.conflictStore.setConflicts();
  }

  public ignoreConflict(ignoreRoomWarnings: boolean = false) {
    if (this.conflictModal.conflicts) {
      const conflicts = this.conflictModal.conflicts.filter((c) => c.type === 'overlappingRooms');
      this.setConflictModalConflicts(conflicts.length > 0 ? conflicts : null);
    }
  }

  public ignoreRoomConflict() {
    this.conflictModal.conflicts = this.conflictModal.conflicts?.filter((c) => c.type !== 'overlappingRooms') ?? null;
  }

  public abortConflict() {
    if (this._lastPickedCard && this._oldCardPlacement) {
      this.pickCard(this._lastPickedCard, this._oldCardPlacement);
    }
    this.setConflictModalConflicts(null);
  }

  public discardPickedCard() {
    const card = this.pickedCard;
    if (card) {
      if (this.context === 'rooms') {
        this.discardPickedCardToOldPlacementForRooms();
      } else {
        if (this._oldCardPlacement) {
          this.discardPickedCardToOldPlacement();
        } else {
          this.discardPickedCardToStack();
        }
        this.root.lockedCards.delete(card.cardUuid);
      }
    }
  }

  public discardPickedCardToOldPlacement() {
    const card = this.pickedCard;
    if (!card || this.context !== 'classes') {
      return;
    }
    this._cardToSwitch = null;
    if (this._oldCardPlacement) {
      this.placeCard(this._oldCardPlacement);
    }
    this.pickedCard = null;
    this._oldCardPlacement = null;
  }

  public discardPickedCardToOldPlacementForRooms() {
    const card = this.pickedCard;
    if (!card || this.context !== 'rooms') {
      return;
    }
    // Verschiebemodus: den beim Aufnehmen entfernten Raum wiederherstellen (Escape/Abbruch =
    // Zustand bleibt unverändert). Kopiermodus: es wurde nichts entfernt → nichts zu tun.
    if (!this._pickIsCopy && this._removedRoomOnPick) {
      const _card = this.root.getCardByUuid(card.cardUuid);
      if (_card) this.root.setRoomsForCards([_card], [this._removedRoomOnPick], true);
    }
    this.pickedCard = null;
    this._removedRoomOnPick = null;
    this._pickIsCopy = false;
    this._oldCardRooms = new Map();
    this._oldRoomUuidToReplace = null;
  }

  public discardPickedCardToRoomsStack() {
    const card = this.pickedCard;
    if (!card) {
      return;
    }

    // Verschiebemodus: der aufgenommene Raum wurde bereits beim Pick entfernt → bleibt entfernt
    // (sind weitere Räume zugewiesen, bleibt die Karte diesen zugeordnet). Kopiermodus: alle bis
    // dahin gesetzten Räume bleiben erhalten. In beiden Fällen wird nur der Pick beendet.
    this.pickedCard = null;
    this._oldCardPlacement = null;
    this._removedRoomOnPick = null;
    this._pickIsCopy = false;
    this._oldRoomUuidToReplace = null;
  }

  public discardPickedCardToStack() {
    const card = this.pickedCard;
    if (!card) {
      return;
    }

    this._oldCardPlacement = null;
    this.pickedCard = null;

    this.root.freeCardsSet.add(card.cardUuid);
    this.root.placedCardsSet.delete(card.cardUuid);
    this.root.placedCardsDay.delete(card.cardUuid);
    this.root.placedCardsTimeGridEntries.delete(card.cardUuid);
    this.root.cardStore.invalidateResolvedCard(card.cardUuid);
    this._cardToSwitch = null;

    void urqlClient
      .mutation<UnplaceTimetableCardsMutation, UnplaceTimetableCardsMutationVariables>(UnplaceTimetableCardsDocument, {
        cardUuids: [card.cardUuid],
      })
      .toPromise();
  }

  public pickCard(card: PickedCardType, cell?: CellItem, fromStack?: boolean, isCopy?: boolean) {
    if (!this.root.isEditable) return;

    if (cell) {
      this._oldCardPlacement = cell;
    }
    this._lastPickedCard = card;

    if (this.context === 'rooms') {
      this.pickRoomCard(card, cell, fromStack, isCopy);
      return;
    }

    if (this.root.lockedCards.has(card.cardUuid)) return;
    this.rowsToHighlight = new Set(
      this.context === 'classes'
        ? (this.root.cardClasses.get(card.cardUuid) ?? new Set())
        : this.context === 'teachers'
          ? (this.root.cardTeacher.get(card.cardUuid) ?? new Set())
          : new Set(),
    );

    if (cell) {
      // Capture cards sharing resources in the old slot BEFORE removing the card
      this._oldSlotCardUuids = this.root.conflictStore._getAffectedCardsForCard(card.cardUuid);
      this._oldSlotCardUuids.delete(card.cardUuid);

      const oldPlaced = this.root.placedCards.get(`${cell.hour.value}:${cell.day.value}`)?.get(card?.cardUuid ?? '');
      if (oldPlaced) {
        this.root.placedCards.set(
          `${cell.hour.value}:${cell.day.value}`,
          new Map(
            [...(this.root.placedCards.get(`${cell.hour.value}:${cell.day.value}`) ?? [])].filter(
              (c) => c[0] !== card?.cardUuid,
            ),
          ),
        );
      }

      this.root.cardStore._removeCardFromPlacedCards(card.cardUuid);
      // Old slot neighbours may now be conflict-free → re-check only them
      this.root.conflictStore.updateConflictsForCards(this._oldSlotCardUuids);
    } else {
      this._oldCardPlacement = null;
      this._oldSlotCardUuids = new Set();
      this.root.freeCardsSet.delete(card?.cardUuid ?? '');
    }
    // Remove the picked card's own conflict entry (it's floating now)
    this.root.conflictStore.cardConflicts.delete(card.cardUuid);
    this.pickedCard = {
      ...card,
      counter: 1,
      position: card.position ?? { x: 0, y: 0 },
      fromStack: fromStack ?? false,
    };
  }

  public pickRoomCard(card: PickedCardType, cell?: CellItem, fromStack?: boolean, isCopy?: boolean) {
    if (!this.root.isEditable) return;
    this._pickIsCopy = isCopy ?? false;
    const room = toJS(this.root.rooms.get(cell?.row.value ?? ''));
    // Verschiebemodus: den Raum der Zeile sofort entfernen (Karte "in der Hand"), für Restore
    // bei Escape merken. Kopiermodus (Shift): nichts entfernen – Räume werden additiv gesetzt.
    if (room && cell && !this._pickIsCopy) {
      // Als RoomItem (Zeile = Raum) merken – kompatibel zu `setRoomsForCards` beim Restore.
      this._removedRoomOnPick = { ...cell.row, group: '', inUse: false };
      this.root.removeRoomsFromCards([{ uuid: card.cardUuid }], [{ value: room.uuid }], false);
    } else {
      this._removedRoomOnPick = null;
    }

    this.pickedCard = {
      ...card,
      counter: 1,
      position: card.position ?? { x: 0, y: 0 },
      fromStack: fromStack ?? false,
    };
  }

  setTimefoldParsedAnalysis(value: ParsedTimefoldAnalysis | null) {
    this.timefoldParsedAnalysis = value;
    this._findingsPlanSignature.timefold = value ? this.root.findingsPlanSignature : null;
  }

  clearTimefoldParsedAnalysis() {
    this.timefoldParsedAnalysis = null;
    this._findingsPlanSignature.timefold = null;
    this.setTimefoldAnalyzeModeActive(false);
  }

  getControlModeResult() {
    return this._controlModeResult;
  }

  /** True when the plan was edited since this check's result was produced → findings may be stale (P2-5). */
  areFindingsStale(kind: 'control' | 'timefold' | 'test'): boolean {
    const snapshot = this._findingsPlanSignature[kind];
    return snapshot !== null && snapshot !== this.root.findingsPlanSignature;
  }

  async generate() {
    if (!this.root.currentVersion?.uuid) {
      return;
    }

    const classesUuids =
      this.selectedRows.length > 0 && this.context === 'classes'
        ? this.selectedRows.map((r) => r.value)
        : this.root.getRows().map((r) => r.value);

    // GENERATE checks for cards the solver may (re)place; OPTIMIZE/ASSIGN_ROOMS act on the existing
    // placement, so their prerequisite is "are there placed (non-locked) cards in scope?".
    const prerequisiteMet =
      this.root.solverStore.task === 'GENERATE'
        ? this.root.cardStore.hasCardsToPlace(classesUuids)
        : this.root.cardStore.hasPlacedCards(classesUuids);

    await this.root.solverStore.generate(this.root.currentVersion.uuid, classesUuids, prerequisiteMet);
  }

  async downloadPdf() {
    const reportType: DisplayReportType =
      this.context === 'rooms'
        ? TimetableReportType.SingleRoom
        : this.context === 'teachers'
          ? TimetableReportType.SingleTeacher
          : TimetableReportType.SingleClass;

    const contextTranslation =
      this.context === 'classes'
        ? i18next.t('classes.title', { count: 2 })
        : this.context === 'rooms'
          ? i18next.t('rooms.title', { count: 2 })
          : this.context === 'teachers'
            ? i18next.t('persons.title', { count: 2 })
            : i18next.t('classes.title', { count: 2 });

    const contextUuids =
      this.selectedRows.length > 0 ? this.selectedRows.map((m) => m.value) : this.root.getRows().map((m) => m.value);

    return await downloadDisplayReport({
      reportType,
      timetableVersionUuid: this.root.currentVersion?.uuid ?? '',
      contextUuids,
      downloadFileName: `${this.root.currentVersion?.timetableName}_Version_${this.root.currentVersion?.versionName}_${contextTranslation}.pdf`,
    });
  }

  setRowHeight(height: number) {
    if (height < 20 || height > 100) return;
    this.rowHeight = height;
  }

  setDefaultRowHeight() {
    this.rowHeight = 32;
  }

  setBoardLayout(layout: BoardLayout) {
    this.boardLayout = layout;
  }

  toggleBoardLayout() {
    this.boardLayout = this.boardLayout === 'stacked' ? 'split' : 'stacked';
  }

  // Höhe des Auswahl-Bereichs im `stacked`-Layout (px), per Trennlinie justiert.
  setBoardTopHeight(height: number) {
    this.boardTopHeight = Math.min(500, Math.max(120, height));
  }

  // Breite des Auswahl-Bereichs im `split`-Layout (% der Board-Breite), per Trennlinie justiert.
  setBoardSplitWidth(width: number) {
    this.boardSplitWidth = Math.min(60, Math.max(20, width));
  }

  // Picker-Anteil im `stacked`-Layout (Breite % des Auswahl-Bereichs), per Trennlinie justiert.
  setBoardPickerWidth(width: number) {
    this.boardPickerWidth = Math.min(80, Math.max(20, width));
  }

  // Picker-Anteil im `split`-Layout (Höhe % des Auswahl-Bereichs), per Trennlinie justiert.
  setBoardPickerHeight(height: number) {
    this.boardPickerHeight = Math.min(80, Math.max(20, height));
  }

  setShowPreviewMap(show: boolean) {
    this.showPreviewMap = show;
  }

  toggleShowPreviewMap() {
    this.showPreviewMap = !this.showPreviewMap;
    this.equalizeBoardPanels();
  }

  setPreviewMapContext(context: PreviewMapContext) {
    this.previewMapContext = context;
    this.previewMapAuto = false;
  }

  setPreviewMapAuto(auto: boolean) {
    this.previewMapAuto = auto;
  }

  // Größe der Vorschau-Karte im Auswahl-Bereich (% je Achse), per Trennlinie justiert.
  setBoardPreviewMapWidth(width: number) {
    this.boardPreviewMapWidth = Math.min(70, Math.max(15, width));
  }

  setBoardPreviewMapHeight(height: number) {
    this.boardPreviewMapHeight = Math.min(70, Math.max(15, height));
  }

  setShowCardInfo(show: boolean) {
    this.showCardInfo = show;
  }

  toggleShowCardInfo() {
    this.showCardInfo = !this.showCardInfo;
    this.equalizeBoardPanels();
  }

  setShowTimetablePreview(show: boolean) {
    this.showTimetablePreview = show;
  }

  toggleShowTimetablePreview() {
    this.showTimetablePreview = !this.showTimetablePreview;
    this.equalizeBoardPanels();
  }

  /**
   * Verteilt den Auswahl-Bereich beim Ein-/Ausschalten gleichmäßig: Picker und alle sichtbaren
   * Panels (PreviewMap, Lehrer-, Raumplan, CardInfo) bekommen je 1/N. Möglich dank der
   * Verschachtelung: Picker = 1/N der `panels`, jedes Rest-Panel = 1/(N−1) des `rest` → jedes
   * Element misst exakt 1/N der Gesamtbreite. Manuelles Nachziehen bleibt danach erhalten.
   * Werte werden direkt gesetzt (ohne Clamp), damit die Aufteilung exakt bleibt.
   */
  private equalizeBoardPanels() {
    // Der Stundenplan-Toggle steuert zwei Panels (Lehrer + Raum).
    const restCount = (this.showPreviewMap ? 1 : 0) + (this.showTimetablePreview ? 2 : 0) + (this.showCardInfo ? 1 : 0);
    const total = 1 + restCount; // + Picker
    const pickerPct = 100 / total;
    this.boardPickerWidth = pickerPct;
    this.boardPickerHeight = pickerPct;

    if (restCount > 0) {
      const restPct = 100 / restCount;
      this.boardPreviewMapWidth = restPct;
      this.boardPreviewMapHeight = restPct;
      this.boardTimetablePreviewTeachersWidth = restPct;
      this.boardTimetablePreviewTeachersHeight = restPct;
      this.boardTimetablePreviewRoomsWidth = restPct;
      this.boardTimetablePreviewRoomsHeight = restPct;
    }
  }

  // Größe der beiden Stundenplan-Panels im Auswahl-Bereich (% je Achse), je per Trennlinie justiert.
  setBoardTimetablePreviewTeachersWidth(width: number) {
    this.boardTimetablePreviewTeachersWidth = Math.min(70, Math.max(15, width));
  }

  setBoardTimetablePreviewTeachersHeight(height: number) {
    this.boardTimetablePreviewTeachersHeight = Math.min(70, Math.max(15, height));
  }

  setBoardTimetablePreviewRoomsWidth(width: number) {
    this.boardTimetablePreviewRoomsWidth = Math.min(70, Math.max(15, width));
  }

  setBoardTimetablePreviewRoomsHeight(height: number) {
    this.boardTimetablePreviewRoomsHeight = Math.min(70, Math.max(15, height));
  }

  setTimetablePreviewCellMinHeight(height: number) {
    this.timetablePreviewCellMinHeight = Math.min(60, Math.max(14, height));
  }

  /**
   * Ablage aus der Stundenplan-Vorschau heraus (Lehrer-/Raumplan). Wiederverwendet die normale
   * `placeCard`-Logik (Konflikte, Swap, Default-Raum, Mutation, Stack-Vorlauf), indem ein Cell mit
   * einer Klassen-Zeile der Karte als Repräsentant synthetisiert wird. Im Raumplan wird nach dem
   * Stecken zusätzlich der Raum des aktiven Tabs zugewiesen — die Reihenfolge ist wichtig, damit
   * `setRoomsForCards` die Räume anhand der jetzt gesetzten Steckung in die Slots indiziert.
   */
  /**
   * Ob die Karte in der Vorschau auf diesen Slot abgelegt werden darf – bewusst STRIKTER als am
   * Board: nur wenn die Zelle „wirklich grün" ist. Kein Swap/Tausch, keine sonstige Ausweichlogik.
   * Erlaubt ist eine Ablage, wenn für alle abgedeckten Stunden gilt: die Ressourcen der Karte sind
   * verfügbar (nicht blockiert/eingeschränkt) UND der Slot ist frei ODER nur mit koexistierbaren
   * Karten belegt (Koexistenz-Merge bleibt möglich).
   */
  public isPreviewDroppable(cardUuid: string, duration: number, hourUuid: string, day: Day): boolean {
    // Im Raum-Kontext bewegt das Board keine Zeit → keine Ablage.
    if (this.context === 'rooms') return false;
    if (!this.root.canBeDropped(cardUuid, duration, hourUuid, day)) return false;

    const card = this.root.getCardByUuid(cardUuid);
    const availabilityStore = this.root.getTimeAvailabilityStore();
    if (!card || !availabilityStore) return false;

    const lastFollower = this.root.getFollowers(hourUuid, duration ?? 1)?.pop()?.value ?? hourUuid;
    const coveredHours = this.root.getAllHoursBetween(hourUuid, lastFollower);
    if (coveredHours.length === 0) return false;

    // Ressourcen der Karte (Klassen, Lehrkräfte, Räume) – über sie laufen Verfügbarkeit und Belegung.
    const resourceUuids = new Set<string>();
    card.classes.forEach((c) => resourceUuids.add(c.uuid));
    this.root.cardTeacher.get(cardUuid)?.forEach((teacherUuid) => resourceUuids.add(teacherUuid));
    this.root.cardRooms.get(cardUuid)?.forEach((roomItem) => resourceUuids.add(roomItem.value));

    const pickedCoexistable = isCoexistableCard(card);
    const otherCards = new Set<string>();

    for (const coveredHour of coveredHours) {
      const timeGridEntry = this.root.timeGridEntries?.find((entry) => entry.uuid === coveredHour.value);
      if (!timeGridEntry) return false;
      const time = dayjs(timeGridEntry.start).add(1, 'minute').format('HH:mm');

      for (const resourceUuid of resourceUuids) {
        // Zeitverfügbarkeit: blockiert oder nur eingeschränkt ("maybe") ⇒ nicht grün.
        if (availabilityStore.isResourceBlocked(time, day, resourceUuid)) return false;
        if (availabilityStore.isResourceMaybe(time, day, resourceUuid)) return false;

        // Belegung: fremde Karten in diesem Ressourcen-Slot sammeln.
        this.root.cardsBySlotAndResource.get(`${coveredHour.value}:${day}:${resourceUuid}`)?.forEach((uuid) => {
          if (uuid !== cardUuid) otherCards.add(uuid);
        });
      }
    }

    if (otherCards.size === 0) return true; // frei

    // Belegt: nur erlaubt, wenn Koexistenz-Merge greift (beide Seiten koexistierbar). Sonst wäre es
    // ein Tausch/Konflikt – am Board ok, in der Vorschau bewusst nicht.
    if (!pickedCoexistable) return false;
    return [...otherCards].every((uuid) => {
      const other = this.root.getCardByUuid(uuid);
      return other ? isCoexistableCard(other) : false;
    });
  }

  public placeCardFromPreview(day: DayItem, hour: HourItem, room?: RoomItem) {
    if (!this.root.isEditable) return;
    // Im Raum-Kontext bewegt das Board keine Zeit (nur Raumzuweisung) → Vorschau ist dort nur
    // informativ, kein Ablageziel.
    if (this.context === 'rooms' || !this.pickedCard) return;

    // Nur ablegen, wenn die Zelle wirklich grün ist – kein Swap, keine Ausweichlogik.
    if (!this.isPreviewDroppable(this.pickedCard.cardUuid, this.pickedCard.duration, hour.value, day.value)) return;

    const card = this.root.getCardByUuid(this.pickedCard.cardUuid);
    // Repräsentative Zeile passend zum Board-Kontext, damit `placeCard` (Slot-Logik) korrekt greift:
    // im Lehrer-Board eine Lehrer-Zeile, sonst eine Klassen-Zeile der Karte.
    const rowUuid = this.context === 'teachers' ? card?.teachers[0]?.uuid : card?.classes[0]?.uuid;
    const row = this.root.getRows().find((r) => r.value === rowUuid);
    if (!card || !row) return;

    // Kein Nachrücken der nächsten Stapelkarte in der Vorschau: `fromStack` unterdrücken (der
    // Stack-Vorlauf in `placeCard` hängt genau daran).
    const fromStack = this.pickedCard.fromStack;
    this.pickedCard = { ...this.pickedCard, fromStack: false };

    this.placeCard({ day, row, hour });

    // Hat der Drop nicht gegriffen, liegt dieselbe Karte noch in der Hand: Herkunft wiederherstellen,
    // damit der Ablagestapel ihren Platz weiter freihält.
    if (this.pickedCard?.cardUuid === card.uuid && !this.root.placedCardsSet.has(card.uuid)) {
      this.pickedCard = { ...this.pickedCard, fromStack };
    }

    // Raum nur setzen, wenn der Drop tatsächlich gegriffen hat. `!this.pickedCard` genügt nicht:
    // bei einer Stapel-Karte reicht `placeCard` danach die nächste Karte in die Hand. Das
    // zuverlässige Erfolgssignal ist, dass DIESE Karte jetzt gesteckt ist.
    if (room && this.root.placedCardsSet.has(card.uuid)) {
      this.root.setRoomsForCards([card], [room], false);
    }
  }
}
